'use client';
import './hubspotForm.scss';

import React, { useEffect, useId, useRef, useState } from 'react';

import { Modal } from '@/components/Modal';
import RichText from '@/components/RichText';
import { Button } from '@/components/ui/button';
import type { EmbedBlock as EmbedBlockProps } from '@/payload-types';
import { cn } from '@/utils/cn';

import { BlockSettings } from '../BlockSettings';
import styles from './block.module.scss';

interface HubSpotSubmittedField {
  label: string;
  name: string;
  value: string;
}

type Props = EmbedBlockProps & {
  gutter?: boolean;
  content: Record<string, any>; // RichText. Due to inEditor flag, we add this to the type outside of the config
};

const invertedThemes = ['indigo-800', 'indigo-500', 'mint-800'];

export const EmbedBlock: React.FC<Props> = (props) => {
  const {
    backgroundColor,
    buttonLabel,
    code,
    content,
    gutter = true,
    individualSpacing,
    modalContent,
    modalTitle,
    offCanvasBackgroundColor,
    openOffCanvas,
    spacing,
    spacingBottom,
    spacingTop,
  } = props;
  const [hasMounted, setHasMounted] = useState(false);
  const [embedId] = useState<string | null>(useId());
  const [modalOpen, setModalOpen] = useState(false);
  const [embedCode, setEmbedCode] = useState<string | null>(null);

  const isHubSpotFormRegex = /hbspt\.forms\.create\(\s*{[^}]*}\s*\);/;
  const portalIdRegex = /portalId:\s*"(\d+)"/;
  const formIdRegex = /formId:\s*"([\w-]+)"/;
  const isTalentechIframeRegex = /candidate\.hr-manager\.net/;

  const isHubSpotForm = isHubSpotFormRegex.test(code || '');
  const isTalentechEmbed = isTalentechIframeRegex.test(code || '');
  const portalIdMatch = code?.match(portalIdRegex);
  const formIdMatch = code?.match(formIdRegex);

  const extractIframeSrc = (html: string): string | null => {
    const match = html.match(/<iframe[^>]+src=["']([^"']+)["']/);
    return match ? match[1] : null;
  };

  const talentechIframeSrc = isTalentechEmbed
    ? extractIframeSrc(code || '')
    : null;

  const portalId = portalIdMatch ? portalIdMatch[1] : null;
  const formId = formIdMatch ? formIdMatch[1] : null;

  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const inverted = invertedThemes.includes(
    backgroundColor ? backgroundColor : ''
  );

  const handleModalOpen = () => {
    setModalOpen(true);

    // Lock body scroll
    document.body.style.overflow = 'hidden';
  };

  const handleModalClose = () => {
    setModalOpen(false);

    // Unlock body scroll
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!hasMounted) return;
    const container = containerRef.current;

    if (isHubSpotForm && portalId && formId) {
      const script = document.createElement('script');
      script.src = 'https://js.hsforms.net/forms/v2.js';
      document.body.appendChild(script);

      scriptRef.current = script;

      script.addEventListener('load', () => {
        // @ts-ignore
        if (window.hbspt) {
          // @ts-ignore
          window.hbspt.forms.create({
            portalId: portalId,
            formId: formId,
            target: `[id="${embedId}"]`,
            onFormSubmit: (form: HTMLFormElement) => {
              const fields: HubSpotSubmittedField[] = [];

              const fieldEls = Array.from(
                form.querySelectorAll('.hs-form-field')
              );

              for (const field of fieldEls) {
                const labelEl = field.querySelector('label');

                if (!labelEl) continue;

                const label = labelEl.textContent?.trim().replace('*', '');

                if (!label) continue;

                const inputEl = field.querySelector('input, textarea, select');

                if (!inputEl) continue;

                const value = (inputEl as any).value;

                const name = inputEl.getAttribute('name');

                if (!name) continue;

                fields.push({ label, value, name });
              }
            },
          });
        }
      });
    } else if (isTalentechEmbed && talentechIframeSrc) {
      const script = document.createElement('script');
      script.src =
        'https://cdn-recruiter.hr-manager.net/Scripts/iframeResizer.min.js';
      script.async = true;

      script.onload = () => {
        if (window.iFrameResize && containerRef.current?.firstChild) {
          window.iFrameResize({ log: true });
        }
      };

      document.body.appendChild(script);
      scriptRef.current = script;

      return () => {
        if (scriptRef.current) {
          document.body.removeChild(scriptRef.current);
        }
      };
    } else if (code) {
      setEmbedCode(code);
    }

    return () => {
      if (scriptRef.current) {
        document.body.removeChild(scriptRef.current);
        scriptRef.current = null;
      }

      if (container) {
        container.innerHTML = '';
      }
    };
  }, [
    code,
    isHubSpotForm,
    portalId,
    formId,
    embedId,
    hasMounted,
    isTalentechEmbed,
    talentechIframeSrc,
  ]);

  if (!code) return null;

  if (!hasMounted) return null;

  return (
    <>
      <BlockSettings
        backgroundColor={backgroundColor}
        individualSpacing={individualSpacing}
        spacing={spacing}
        spacingBottom={spacingBottom}
        spacingTop={spacingTop}
      >
        <div className={cn(gutter ? 'container' : '')}>
          {!openOffCanvas ? (
            <>
              {content && (
                <RichText
                  content={content}
                  className={styles.content}
                  enableGutter={false}
                />
              )}
              <div
                id={embedId || ''}
                className={cn('hubspot-form', styles.embed)}
                ref={containerRef}
              >
                {isHubSpotForm && !embedCode && null}

                {isTalentechEmbed && talentechIframeSrc && (
                  <iframe
                    src={talentechIframeSrc}
                    width='100%'
                    style={{ border: 'none' }}
                  />
                )}

                {!isHubSpotForm && !isTalentechEmbed && embedCode && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: embedCode,
                    }}
                  />
                )}
              </div>
            </>
          ) : (
            <div
              className={cn(
                styles.triggerArea,
                {
                  [styles.inverted]: inverted,
                },
                offCanvasBackgroundColor
                  ? `${styles[`triggerArea--${offCanvasBackgroundColor}`]}`
                  : ''
              )}
            >
              {content && (
                <RichText
                  content={content}
                  className={styles.content}
                  enableGutter={false}
                />
              )}
              {buttonLabel && (
                <Button
                  className='button button--level-1 button--medium'
                  onClick={handleModalOpen}
                >
                  {buttonLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      </BlockSettings>
      <Modal
        clickHandler={handleModalClose}
        content={modalContent || {}}
        title={modalTitle || ''}
        modalOpen={modalOpen}
      >
        <div id={embedId || ''} className={'hubspot-form'} ref={containerRef}>
          {isHubSpotForm && !embedCode && null}

          {isTalentechEmbed && talentechIframeSrc && (
            <iframe
              src={talentechIframeSrc}
              width='100%'
              style={{ border: 'none' }}
            />
          )}

          {!isHubSpotForm && !isTalentechEmbed && embedCode && (
            <div
              dangerouslySetInnerHTML={{
                __html: embedCode,
              }}
            />
          )}
        </div>
      </Modal>
    </>
  );
};
